import React, { useEffect, useState } from 'react';
import { detectLanguage } from '../translations';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { getTranslation } from '../translations';

const RefundPolicy: React.FC = () => {
  const [lang, setLang] = useState('en');
  const [t, setT] = useState(getTranslation('en'));

  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    const detectedLang = savedLang || detectLanguage();
    setLang(detectedLang);
    setT(getTranslation(detectedLang));
  }, []);

  const handleLanguageChange = (newLang: string) => {
    setLang(newLang);
    setT(getTranslation(newLang));
    localStorage.setItem('preferredLanguage', newLang);
  };

  const isKorean = lang === 'ko';

  return (
    <div className="font-sans antialiased bg-gray-50">
      <Navigation t={t} currentLang={lang} onLanguageChange={handleLanguageChange} />
      
      <div className="max-w-4xl mx-auto px-4 py-24 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">
          {isKorean ? '환불 정책' : 'Refund Policy'}
        </h1>
        
        <div className="prose prose-lg max-w-none">
          <p className="text-gray-600 mb-6">
            {isKorean 
              ? '최종 업데이트: 2025년 1월 30일'
              : 'Last Updated: January 30, 2025'}
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
            <p className="text-blue-700">
              <strong>{isKorean ? '14일 환불 정책' : '14-Day Refund Policy'}</strong><br />
              {isKorean
                ? '구매 후 14일 이내에 서비스를 사용하지 않은 경우 전액 환불이 가능합니다. 글로벌 소비자 보호법을 준수합니다.'
                : 'Full refund available if service is unused within 14 days of purchase. Compliant with global consumer protection laws.'}
            </p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
            <p className="text-yellow-800">
              <strong>{isKorean ? '중요 고지사항' : 'Important Notice'}</strong><br />
              {isKorean
                ? '디지털 서비스 특성상, 서비스 이용을 시작하면 철회권이 소멸됩니다. 구매 시 이에 동의하는 것으로 간주됩니다.'
                : 'Due to the nature of digital services, your right of withdrawal expires once you start using the service. By purchasing, you agree to this condition.'}
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '1. 무료 체험' : '1. Free Trial'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? 'AivueBiz는 모든 사용자에게 무료 플랜을 제공합니다. 신용카드 정보 없이 서비스를 먼저 체험해보실 수 있습니다. 무료 플랜에서는 일일 50개 메시지, 20분 영상통화 등의 기능을 이용할 수 있습니다.'
                : 'AivueBiz offers a free plan for all users. You can try our service without providing credit card information. The free plan includes 50 messages per day, 20 minutes of video calls, and more.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '2. 환불 가능 조건' : '2. Refund Eligibility'}
            </h2>
            
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '다음 조건에 해당하는 경우 환불을 요청할 수 있습니다:'
                : 'You may request a refund under the following conditions:'}
            </p>
            
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                {isKorean
                  ? '구매 후 14일 이내 서비스 미사용 시 (사용 시작 전)'
                  : 'Within 14 days of purchase if service is unused (before service activation)'}
              </li>
              <li>
                {isKorean
                  ? '중복 결제 또는 실수로 인한 이중 구독'
                  : 'Duplicate payments or accidental double subscriptions'}
              </li>
              <li>
                {isKorean
                  ? '서비스가 설명된 대로 작동하지 않는 경우'
                  : 'Service not functioning as described'}
              </li>
              <li>
                {isKorean
                  ? '기술적 문제로 서비스 이용이 불가능한 경우'
                  : 'Technical issues preventing service usage'}
              </li>
            </ul>
            
            <p className="text-gray-600 mt-4 p-3 bg-gray-50 rounded">
              <strong>{isKorean ? '서비스 사용의 정의:' : 'Definition of Service Usage:'}</strong><br />
              {isKorean
                ? '채팅방 개설, 메시지 발송, 영상 통화, 파일 전송, 음성인식(STT) 기능 사용 등 유료 기능을 1회 이상 사용한 경우 "서비스 사용"으로 간주됩니다. 사용량은 시스템에서 자동으로 추적됩니다.'
                : 'Service is considered "used" when any paid feature (creating chat rooms, messaging, video calls, file transfers, speech-to-text, etc.) has been used at least once. Usage is automatically tracked by our system.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '3. 환불 절차' : '3. Refund Process'}
            </h2>
            
            <ol className="list-decimal pl-6 text-gray-700 space-y-2">
              <li>
                {isKorean
                  ? 'support@aivuebiz.com으로 환불 요청 이메일 발송'
                  : 'Send a refund request email to support@aivuebiz.com'}
              </li>
              <li>
                {isKorean
                  ? '다음 정보를 포함해주세요:'
                  : 'Include the following information:'}
                <ul className="list-disc pl-6 mt-2">
                  <li>{isKorean ? '등록된 이메일 주소' : 'Your registered email address'}</li>
                  <li>{isKorean ? '구매 날짜' : 'Date of purchase'}</li>
                  <li>{isKorean ? 'LemonSqueezy 거래 ID (영수증에서 확인)' : 'LemonSqueezy transaction ID (from your receipt)'}</li>
                  <li>{isKorean ? '환불 사유' : 'Reason for refund request'}</li>
                </ul>
              </li>
              <li>
                {isKorean
                  ? '시스템이 자동으로 환불 자격을 확인합니다 (사용량 및 기간 체크)'
                  : 'System automatically verifies refund eligibility (usage and time period check)'}
              </li>
              <li>
                {isKorean
                  ? '자격 충족 시 영업일 기준 2-3일 이내에 환불 처리'
                  : 'If eligible, refund processed within 2-3 business days'}
              </li>
              <li>
                {isKorean
                  ? '승인 후 5-10 영업일 이내에 환불이 처리됩니다 (결제 방법에 따라 상이)'
                  : 'Once approved, refunds are processed within 5-10 business days (varies by payment method)'}
              </li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '4. 구독 취소' : '4. Subscription Cancellation'}
            </h2>
            
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '구독 취소는 언제든지 가능하며, 다음 결제 주기부터 자동 갱신이 중지됩니다. 현재 결제 기간이 종료될 때까지 서비스를 계속 이용할 수 있습니다.'
                : 'You can cancel your subscription at any time, which will stop auto-renewal from the next billing cycle. You will continue to have access to the service until the end of your current billing period.'}
            </p>
            
            <p className="text-gray-700 mb-4">
              <strong>{isKorean ? '취소 방법:' : 'How to cancel:'}</strong>
            </p>
            
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                {isKorean
                  ? 'AivueBiz Desktop 앱의 결제 섹션에서 구독 취소'
                  : 'Cancel subscription in the payment section of AivueBiz Desktop app'}
              </li>
              <li>
                {isKorean
                  ? 'LemonSqueezy 고객 포털에서 직접 취소'
                  : 'Cancel directly through LemonSqueezy customer portal'}
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '5. 플랜 변경' : '5. Plan Changes'}
            </h2>
            
            <h3 className="text-xl font-semibold mb-3">
              {isKorean ? '업그레이드' : 'Upgrades'}
            </h3>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '상위 플랜으로 업그레이드하면 즉시 새로운 기능을 이용할 수 있으며, 차액은 일할 계산되어 청구됩니다.'
                : 'When you upgrade to a higher plan, new features are available immediately and the price difference is prorated.'}
            </p>

            <h3 className="text-xl font-semibold mb-3">
              {isKorean ? '다운그레이드' : 'Downgrades'}
            </h3>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '하위 플랜으로 다운그레이드하면 다음 결제 주기부터 적용됩니다. 현재 결제 기간이 끝날 때까지 현재 플랜의 모든 기능을 계속 사용할 수 있습니다.'
                : 'When you downgrade to a lower plan, it takes effect from the next billing cycle. You can continue using all features of your current plan until the end of the current billing period.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '6. 결제 처리' : '6. Payment Processing'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '모든 결제는 LemonSqueezy를 통해 안전하게 처리됩니다. 결제 관련 문제는 LemonSqueezy 고객 지원팀(lemonsqueezy.com/help)에 직접 문의하실 수도 있습니다.'
                : 'All payments are securely processed through LemonSqueezy. For payment-related issues, you can also contact LemonSqueezy customer support directly at lemonsqueezy.com/help.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '7. 지역별 법적 준수' : '7. Regional Legal Compliance'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '본 환불 정책은 다음 법규를 준수합니다:'
                : 'This refund policy complies with the following regulations:'}
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>
                {isKorean
                  ? '유럽연합: EU 소비자권리지침 (14일 철회권)'
                  : 'European Union: EU Consumer Rights Directive (14-day withdrawal right)'}
              </li>
              <li>
                {isKorean
                  ? '대한민국: 전자상거래 등에서의 소비자보호에 관한 법률'
                  : 'South Korea: Act on Consumer Protection in Electronic Commerce'}
              </li>
              <li>
                {isKorean
                  ? '미국: FTC 규정 및 주별 소비자보호법'
                  : 'United States: FTC regulations and state consumer protection laws'}
              </li>
              <li>
                {isKorean
                  ? '일본: 특정상거래법'
                  : 'Japan: Act on Specified Commercial Transactions'}
              </li>
            </ul>
            <p className="text-gray-700 mt-4 p-3 bg-blue-50 rounded">
              <strong>{isKorean ? '참고:' : 'Note:'}</strong> {isKorean
                ? '귀하의 지역 법률이 더 유리한 조건을 제공하는 경우, 해당 법률이 우선 적용됩니다.'
                : 'If your local laws provide more favorable terms, those laws will prevail.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '8. 정책 변경' : '8. Policy Changes'}
            </h2>
            <p className="text-gray-700 mb-4">
              {isKorean
                ? '이 환불 정책은 사전 통지와 함께 변경될 수 있습니다. 구매 시점의 정책이 해당 구매에 적용됩니다.'
                : 'This refund policy may be updated with prior notice. The policy at the time of purchase applies to that purchase.'}
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">
              {isKorean ? '9. 연락처' : '9. Contact Us'}
            </h2>
            <p className="text-gray-700">
              {isKorean
                ? '환불 또는 결제 관련 문의사항이 있으시면 아래로 연락 주세요:'
                : 'If you have any questions about refunds or payments, please contact us:'}
            </p>
            <p className="text-gray-700 mt-4">
              <strong>Groove Inc.</strong><br />
              Email: support@aivuebiz.com<br />
              {isKorean 
                ? '응답 시간: 영업일 기준 24-48시간'
                : 'Response time: 24-48 hours on business days'}
            </p>
          </section>
        </div>
      </div>
      
      <Footer t={t} />
    </div>
  );
};

export default RefundPolicy;